import React, { useEffect, useContext } from "react";
import NetwalaModal from "../Items/Modal/Index";
import { Col, Row, Typography, Form, Input, InputNumber } from "antd";
import "./styles/RequestIPTVModal.css";
import NetwalaButton from "../Items/Button/Index";
import { ButtonProps } from "../../helpers/constants";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import BalanceContext from "../../context/BalanceContext";
import ServiceIcon from "./../../assets/svgs/OtherServicesInvoiceIconWhite.svg";

const { TextArea } = Input;

const OtherServiceInvoiceModal: React.FC<OtherServiceInvoiceModalProps> = ({
  show,
  handleClose,
  connection,
}) => {
  const { value, setValue } = useContext(BalanceContext);
  const { openNotification, contextHolder } = useMultiNotification();
  const [form] = Form.useForm();

  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<OtherServiceInvoiceApiProp>(
    "invoices/createOtherServiceInvoice",
    true
  );

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0) {
      openNotification("error", "Operation Failed", apiError);
    }
  }, [apiError, apiErrorCount]);

  useEffect(() => {
    if (apiResponse !== null) {
      const { message } = apiResponse;
      if (message) {
        setValue(value + 1);
        openNotification("success", "Success", message);
        form.resetFields();
        handleClose(true);
      }
    }
  }, [apiResponse]);

  const onFinish = (values: any) => {
    const { amount, comments, invoicePaidAmount } = values;
    const dataToSend: OtherServiceInvoiceApiProp = {
      intPkConnectionID: connection.intPkConnectionID,
      amount: Number(amount),
      comments: comments.trim(),
      invoicePaidAmount: invoicePaidAmount ? Number(invoicePaidAmount) : 0,
    };
    postApiData(dataToSend);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <>
      <NetwalaModal
        title="Sale Other Service"
        titleImage={ServiceIcon}
        titleImageStyle={{ width: "50px", height: "50px" }}
        headerStyle={{
          backgroundColor: "#67318C",
          color: "#FFFFFF",
          fontSize: "16px",
          fontWeight: "400",
          height: "50px",
        }}
        open={show}
        onCancel={() => {
          form.resetFields();
          handleClose(false);
        }}
      >
        {contextHolder}

        <Row>
          <Col span={24} className="iptv-modal-connection-details-container">
            <Typography className="user-full-name">
              {connection.firstName + " " + connection.lastName}
            </Typography>
            <Typography className="user-phone">
              {connection.phoneNumber}
            </Typography>
            <Typography className="user-address">
              {`${connection.address}, ${connection.areaName}`}
            </Typography>
            {connection.connectionType === "INTERNET" &&
              connection.username && (
                <Typography className="user-phone" style={{ marginTop: "8px" }}>
                  Username: {connection.username}
                </Typography>
              )}
          </Col>

          <Col span={24} className="iptv-modal-package-details">
            <Form
              form={form}
              requiredMark={false}
              style={{ width: "100%" }}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              name="OtherServiceInvoiceForm"
            >
              <Form.Item
                label={
                  <Typography className="form-label-text">
                    Service Amount <span style={{ color: "red" }}>*</span>
                  </Typography>
                }
                name="amount"
                rules={[
                  {
                    required: true,
                    message: "Please enter the service amount",
                  },
                  {
                    type: "number",
                    min: 0,
                    message: "Amount must be greater than or equal to 0",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Enter service amount"
                  prefix="Rs. "
                  min={0}
                  step={1}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Typography className="form-label-text">
                    Amount Paid (Optional)
                  </Typography>
                }
                name="invoicePaidAmount"
                dependencies={["amount"]}
                rules={[
                  {
                    type: "number",
                    min: 0,
                    message: "Paid amount must be greater than or equal to 0",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const totalAmount = getFieldValue("amount");
                      if (!value || !totalAmount || value <= totalAmount) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Paid amount cannot exceed total amount")
                      );
                    },
                  }),
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Enter paid amount (default: 0)"
                  prefix="Rs. "
                  min={0}
                  step={1}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Typography className="form-label-text">
                    Comments <span style={{ color: "red" }}>*</span>
                  </Typography>
                }
                name="comments"
                rules={[
                  {
                    required: true,
                    message: "Please enter comments about this service",
                  },
                  {
                    min: 1,
                    message: "Comments cannot be empty",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter service description or comments..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "30px",
                  marginBottom: "0px",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <NetwalaButton
                    backgroundColor={ButtonProps.background.SECONDARY}
                    height={ButtonProps.height.STANDARD}
                    width={ButtonProps.width.ONE24}
                    textColor={ButtonProps.color.BLACK}
                    onClick={() => {
                      form.resetFields();
                      handleClose(false);
                    }}
                    text="Cancel"
                    borderColor="#D7D7D7"
                    disabled={apiLoading}
                  />
                  <NetwalaButton
                    backgroundColor={ButtonProps.background.STANDARD}
                    height={ButtonProps.height.STANDARD}
                    width={ButtonProps.width.ONE24}
                    textColor={ButtonProps.color.STANDARD}
                    htmlType="submit"
                    onClick={() => {}}
                    text="Create Invoice"
                    borderColor="#67318C"
                    disabled={apiLoading}
                  />
                </div>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </NetwalaModal>
    </>
  );
};

export default OtherServiceInvoiceModal;
